import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Brain, 
  MessageSquare, 
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { LinkLogo } from '@/components/ui/LinkLogo';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const navItems = [
  { path: '/dashboard', label: 'Command Center', icon: LayoutDashboard },
  { path: '/claims', label: 'Claims Tracker', icon: FileText },
  { path: '/payer-intelligence', label: 'Payer Intelligence', icon: Brain },
  { path: '/admin/audit-log', label: 'Audit Logs', icon: MessageSquare },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthContext();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    toast.success('Securely Signed Out', {
      description: 'Your session has been terminated and all cached data cleared.',
    });
  };

  // Auto-collapse on tablet (768-1024px)
  useEffect(() => {
    const checkTablet = () => {
      const width = window.innerWidth;
      const tablet = width >= 768 && width < 1024;
      setIsTablet(tablet);
      if (tablet) {
        setCollapsed(true);
      }
    };

    checkTablet();
    window.addEventListener('resize', checkTablet);
    return () => window.removeEventListener('resize', checkTablet);
  }, []);

  const handleLogoClick = () => {
    navigate('/');
  };

  const isCollapsed = collapsed || isTablet;

  return (
    <aside 
      className={cn(
        "h-screen bg-sidebar border-r border-sidebar-border flex flex-col transition-all duration-300",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo - Clickable to Home */}
      <div className="h-16 flex items-center px-4 border-b border-sidebar-border">
        <button 
          onClick={handleLogoClick}
          className={cn(
            "flex items-center gap-3 group cursor-pointer transition-all duration-200 hover:opacity-80 min-h-[44px]",
            isCollapsed && "justify-center w-full"
          )}
        >
          <LinkLogo showText={!isCollapsed} size="sm" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto scrollbar-thin">
        {navItems.map(({ path, label, icon: Icon }) => {
          const isActive = location.pathname === path;
          return (
            <NavLink
              key={path}
              to={path}
              title={isCollapsed ? label : undefined}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group min-h-[44px]",
                isActive 
                  ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                  : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground",
                isCollapsed && "justify-center px-0"
              )}
            >
              <Icon className={cn(
                "w-5 h-5 flex-shrink-0 transition-colors",
                isActive ? "text-primary" : "text-muted-foreground group-hover:text-primary"
              )} />
              {!isCollapsed && (
                <span className="text-sm font-medium">{label}</span>
              )}
              {isActive && !isCollapsed && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Logout Button */}
      <div className="p-2 border-t border-sidebar-border">
        <button
          onClick={handleSignOut}
          title={isCollapsed ? 'Sign Out' : undefined}
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-destructive hover:bg-destructive/10 transition-colors min-h-[44px] font-medium",
            isCollapsed && "justify-center px-0"
          )}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span className="text-sm">Sign Out</span>}
        </button>
      </div>

      {/* Collapse Button - Only show on desktop */}
      {!isTablet && (
        <div className="p-2 border-t border-sidebar-border">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/50 transition-colors min-h-[44px]"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            {!collapsed && <span className="text-xs">Collapse</span>}
          </button>
        </div>
      )}
    </aside>
  );
}